import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import PageShell from '@components/pages/PageShell';
import {
    ArticleCard,
    DownloadGrid,
    GalleryGrid,
    SectionLabel,
    type DownloadItem,
} from '@components/pages/ResourceSections';
import Newsletter from '@components/landing/Newsletter';
import type { DocCollection } from '@/data/resources';
import {
    getProject,
    programLabel,
    projectArticles,
    projectDocuments,
    projectGallery,
    projectStories,
    PROJECTS,
} from '@/data/projects';
import Seo from '@components/Seo';
import JsonLd from '@components/JsonLd';
import { breadcrumbSchema } from '@/lib/schema';
import { PageSkeleton, useSiteLoading } from '@components/Skeleton';
import { Img } from '@/components/Img';

/* ── Section wrapper ──────────────────────────────────────────────────── */

function Band({
    id,
    label,
    title,
    action,
    tone = 'white',
    children,
}: {
    id?: string;
    label: string;
    title?: string;
    action?: { to: string; text: string };
    tone?: 'white' | 'grey';
    children: React.ReactNode;
}) {
    return (
        <section id={id} className={`${tone === 'grey' ? 'bg-[#f4f4f4]' : 'bg-white'} py-14 lg:py-20`}>
            <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <SectionLabel>{label}</SectionLabel>
                        {title && (
                            <h2 className="font-primary mt-4 max-w-[760px] text-[28px] font-bold leading-[1.15] tracking-[-1px] text-[#08080c] lg:text-[38px]">
                                {title}
                            </h2>
                        )}
                    </div>
                    {action && (
                        <Link
                            to={action.to}
                            className="shrink-0 self-start text-[13px] font-bold text-[#f60] hover:underline sm:self-auto"
                        >
                            {action.text} &rarr;
                        </Link>
                    )}
                </div>
                {children}
            </div>
        </section>
    );
}

/* ── Documents, split by kind ─────────────────────────────────────────── */

const DOC_TABS: { id: DocCollection; label: string; basePath: string }[] = [
    { id: 'publications', label: 'Publications', basePath: '/resources/publications' },
    { id: 'reports', label: 'Reports', basePath: '/resources/reports' },
    { id: 'infographics', label: 'Infographics', basePath: '/resources/infographics' },
];

/**
 * Publications, reports and infographics attached to the project. They share the
 * download card, so one tabbed band covers all three rather than three grids.
 */
function ProjectDocuments({ docs }: { docs: Record<DocCollection, DownloadItem[]> }) {
    const [tab, setTab] = useState(DOC_TABS[0]);
    const shown = docs[tab.id];

    return (
        <>
            <div role="tablist" aria-label="Attached documents" className="mt-6 flex flex-wrap gap-2">
                {DOC_TABS.map((t) => {
                    const active = tab.id === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setTab(t)}
                            className={`rounded-full px-5 py-2.5 text-[13px] font-bold transition-colors ${
                                active
                                    ? 'bg-[#08080c] text-white'
                                    : 'border border-[#e2e2e2] bg-white text-[#5e5e5e] hover:border-[#08080c] hover:text-[#08080c]'
                            }`}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {shown.length ? (
                <DownloadGrid items={shown} basePath={tab.basePath} />
            ) : (
                <p className="mt-8 rounded-[10px] border border-dashed border-[#d0d0d0] px-6 py-10 text-center text-[13px] text-[#5e5e5e]">
                    No {tab.label.toLowerCase()} attached to this project yet.
                </p>
            )}
        </>
    );
}

/* ── Page ─────────────────────────────────────────────────────────────── */

export default function ProjectDetail() {
    const { slug } = useParams();
    const project = getProject(slug);

    const loading = useSiteLoading();

    // The pools are empty until the feed lands, so "no such page" is not a
    // conclusion that can be drawn yet — drawing it sent every programme,
    // product and project link to the 404, and a redirect cannot be undone.
    if (!project) {
        if (loading) {
            return (
                <PageShell>
                    <div className="bg-white pb-14 pt-[120px] lg:pb-20 lg:pt-[160px]">
                        <PageSkeleton />
                    </div>
                </PageShell>
            );
        }
        return <Navigate to="/404" replace />;
    }

    const stories = projectStories(project);
    const articles = projectArticles(project);
    const gallery = projectGallery(project);
    const docs = projectDocuments(project);
    const hasDocs = DOC_TABS.some((t) => docs[t.id].length > 0);
    const siblings = PROJECTS.filter(
        (p) => p.slug !== project.slug && p.programs.some((x) => project.programs.includes(x)),
    );

    const jump = [
        { id: 'problem', label: 'The problem' },
        { id: 'solution', label: 'Our solution' },
        stories.length && { id: 'stories', label: 'Stories' },
        articles.length && { id: 'articles', label: 'Articles' },
        gallery.length && { id: 'gallery', label: 'Gallery' },
        hasDocs && { id: 'documents', label: 'Documents' },
    ].filter(Boolean) as { id: string; label: string }[];

    return (
        <PageShell>
            <JsonLd
                id="breadcrumb"
                data={breadcrumbSchema([
                    { name: 'Home', path: '/' },
                    { name: 'Projects', path: '/projects' },
                    { name: project.name, path: `/projects/${project.slug}` },
                ])}
            />
            <Seo
                // `/projects/podus-ai` and `/innovations/podus-ai` are both about the
                // product; naming the programme keeps the two apart in a result list.
                title={`${project.name}: ${programLabel(project.programs[0] ?? '')}`.replace(/: $/, '')}
                description={project.tagline}
                image={project.image}
                canonical={`/projects/${project.slug}`}
            />
            {/* ── Hero ─────────────────────────────────────────────────── */}
            <section className="relative overflow-hidden bg-[#0a0a0a]">
                <Img
                    src={project.image}
                    alt=""
                    priority
                    className="absolute inset-0 size-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/55" />

                <div className="relative mx-auto max-w-[1640px] px-5 pb-14 pt-[130px] lg:px-[140px] lg:pb-20 lg:pt-[170px]">
                    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-[13px]">
                        <Link to="/projects" className="text-white/60 hover:text-[#f60]">
                            Projects
                        </Link>
                        {project.programs.map((slug) => (
                            <span key={slug} className="flex items-center gap-2">
                                <span className="text-white/30">/</span>
                                <Link to={`/focus-areas/${slug}`} className="text-white/60 hover:text-[#f60]">
                                    {programLabel(slug)}
                                </Link>
                            </span>
                        ))}
                    </nav>

                    <span className="mt-6 flex size-14 items-center justify-center rounded-[14px] bg-white/95 p-2.5 lg:size-16">
                        <Img src={project.logo} alt="" className="size-full object-contain" />
                    </span>

                    <h1 className="font-primary mt-5 max-w-[900px] text-[44px] font-bold leading-[1.05] tracking-[-2px] text-white lg:text-[68px] lg:tracking-[-3px]">
                        {project.name}
                        <span className="text-[#f60]">.</span>
                    </h1>
                    <p className="mt-5 max-w-[620px] text-[14px] leading-[1.75] text-white/80 lg:text-[15px]">
                        {project.tagline}
                    </p>

                    {project.website && (
                        <a
                            href={project.website}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:border-[#f60] hover:text-[#f60]"
                        >
                            {project.website.replace(/^https?:\/\//, '')}
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path
                                    d="M7 17 17 7M9 7h8v8"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </a>
                    )}

                    {/* At a glance */}
                    <dl className="mt-10 grid max-w-[900px] grid-cols-2 gap-x-8 gap-y-6 lg:grid-cols-4">
                        {[
                            { k: 'Status', v: project.status },
                            { k: 'Running since', v: project.started },
                            { k: 'Where', v: project.coverage },
                            {
                                k: project.programs.length > 1 ? 'Programmes' : 'Programme',
                                v: project.programs.map(programLabel).join(', '),
                            },
                        ].map((m) => (
                            <div key={m.k}>
                                <dt className="text-[11px] font-bold tracking-[0.4px] text-white/45 uppercase">
                                    {m.k}
                                </dt>
                                <dd className="mt-1.5 text-[14px] font-semibold leading-[1.5] text-white">{m.v}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </section>

            {/* ── Jump bar ─────────────────────────────────────────────── */}
            <nav
                aria-label="On this page"
                className="scrollbar-hide sticky top-[72px] z-30 overflow-x-auto border-b border-[#e2e2e2] bg-white/95 backdrop-blur"
            >
                <div className="mx-auto flex max-w-[1640px] gap-6 px-5 lg:px-[140px]">
                    {jump.map((j) => (
                        <a
                            key={j.id}
                            href={`#${j.id}`}
                            className="shrink-0 border-b-2 border-transparent py-4 text-[13px] font-semibold text-[#5e5e5e] transition-colors hover:border-[#f60] hover:text-[#08080c]"
                        >
                            {j.label}
                        </a>
                    ))}
                </div>
            </nav>

            {/* ── Headline numbers ─────────────────────────────────────── */}
            <section className="bg-white pt-14 lg:pt-20">
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <div className="grid grid-cols-1 gap-px overflow-hidden rounded-[10px] bg-[#e2e2e2] sm:grid-cols-3">
                        {project.stats.map((s) => (
                            <div key={s.label} className="bg-[#f9f9f9] px-6 py-8 text-center">
                                <p className="font-primary text-[34px] font-bold tracking-[-1.2px] text-[#08080c] lg:text-[44px]">
                                    {s.value}
                                </p>
                                <p className="mt-1 text-[12px] text-[#5e5e5e]">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Problem ──────────────────────────────────────────────── */}
            <Band id="problem" label="The Problem" title="What this project exists to fix">
                <div className="mt-8 grid gap-8 lg:grid-cols-2 lg:gap-16">
                    {project.problem.map((p, i) => (
                        <p key={i} className="text-[14px] leading-[1.9] text-[#3d3d3d] lg:text-[15px]">
                            {p}
                        </p>
                    ))}
                </div>
            </Band>

            {/* ── Solution + objectives ────────────────────────────────── */}
            <Band id="solution" label="Our Solution" title="What we actually do about it" tone="grey">
                <div className="mt-8 flex flex-col gap-10 lg:flex-row lg:gap-16">
                    <div className="min-w-0 lg:flex-1">
                        {project.solution.map((p, i) => (
                            <p key={i} className="mb-5 text-[14px] leading-[1.9] text-[#3d3d3d] lg:text-[15px]">
                                {p}
                            </p>
                        ))}
                    </div>

                    <div className="lg:w-[400px] lg:shrink-0">
                        <p className="text-[13px] font-bold text-[#08080c]">What we hold it to</p>
                        <ul className="mt-4 flex flex-col gap-4">
                            {project.objectives.map((o) => (
                                <li key={o} className="flex gap-3">
                                    <span className="mt-[7px] size-[7px] shrink-0 rounded-[1px] bg-[#f60]" />
                                    <span className="text-[13px] leading-[1.7] text-[#3d3d3d]">{o}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Band>

            {/* ── Stories ──────────────────────────────────────────────── */}
            {stories.length > 0 && (
                <Band
                    id="stories"
                    label="Stories"
                    title="The people behind the numbers"
                    action={{ to: '/resources/stories', text: 'All stories' }}
                >
                    <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {stories.map((s) => (
                            <ArticleCard key={s.slug} item={s} basePath="/resources/stories" />
                        ))}
                    </div>
                </Band>
            )}

            {/* ── Articles ─────────────────────────────────────────────── */}
            {articles.length > 0 && (
                <Band
                    id="articles"
                    label="Articles"
                    title="Written up from this work"
                    tone="grey"
                    action={{ to: '/resources/blog', text: 'All articles' }}
                >
                    <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {articles.map((a) => (
                            <ArticleCard key={a.slug} item={a} />
                        ))}
                    </div>
                </Band>
            )}

            {/* ── Gallery ──────────────────────────────────────────────── */}
            {gallery.length > 0 && (
                <Band
                    id="gallery"
                    label="Gallery"
                    title="The work as it looks"
                    action={{ to: '/resources/gallery', text: 'Full gallery' }}
                >
                    <GalleryGrid items={gallery} />
                </Band>
            )}

            {/* ── Documents ────────────────────────────────────────────── */}
            {hasDocs && (
                <Band id="documents" label="Documents" title="Reports, publications and infographics" tone="grey">
                    <ProjectDocuments docs={docs} />
                </Band>
            )}

            {/* ── Siblings ─────────────────────────────────────────────── */}
            {siblings.length > 0 && (
                <Band label="Also in this programme" action={{ to: '/projects', text: 'All projects' }}>
                    <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {siblings.map((s) => (
                            <Link
                                key={s.slug}
                                to={`/projects/${s.slug}`}
                                className="group flex items-center gap-4 rounded-[10px] bg-[#f5f5f5] p-4 transition-colors hover:bg-[#efefef]"
                            >
                                <span className="size-[64px] shrink-0 overflow-hidden rounded-[6px] bg-[#d9d9d9]">
                                    <Img src={s.image} alt="" className="size-full object-cover" />
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-[14px] font-bold text-[#08080c]">{s.name}</span>
                                    <span className="mt-1 line-clamp-2 block text-[12px] leading-[1.6] text-[#5e5e5e]">
                                        {s.tagline}
                                    </span>
                                </span>
                            </Link>
                        ))}
                    </div>
                </Band>
            )}

            <Newsletter />
        </PageShell>
    );
}
